import { useEffect, useState } from 'react';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight, MdOutlineAddBox } from 'react-icons/md';
import { ConfirmDelete, MwButton, MwSpinner } from '../../ui';
import { useFetch, useCurrent, useDelete } from '../../../hooks';
import ListCategoriesItem from './ListCategoriesItem';
import NoDataFound from '../../ui/NoDataFound';
import CardHeadeWithActions from '../../ui/CardHeadeWithActions';
import { useTranslation } from 'react-i18next';

const DEFAULT_ITEMS_PER_PAGE = 10;

const ListCategories = ({ idSelected, setIdSelected, setMode }) => {
    const [t, i18n] = useTranslation('global');
    const { currentTenantId, currentCompanyId } = useCurrent();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
    const {
        data: categories,
        loading,
        error,
        refreshHandler,
    } = useFetch(`${process.env.REACT_APP_PRODUCTS_API_SERVER_BASE_URL}/categories/${currentTenantId}/${currentCompanyId}`);
    const {
        data: deleteResult,
        loading: deleteLoading,
        deleteItem,
    } = useDelete();

    const allCategories = categories?.data || [];
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems =
        itemsPerPage === DEFAULT_ITEMS_PER_PAGE
            ? allCategories.slice(indexOfFirstItem, indexOfLastItem)
            : allCategories;
    const totalPages = allCategories.length
        ? Math.ceil(allCategories.length / itemsPerPage)
        : 0;

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const editHandeler = (id) => {
        setIdSelected(id);
        setMode('Edit');
    };

    const deleteHandeler = (id) => {
        setIdSelected(id);
        setShowConfirmDelete(true);
    };

    const confirmDelete = () => {
        deleteItem(`${process.env.REACT_APP_PRODUCTS_API_SERVER_BASE_URL}/categories/delete/${idSelected}`);
    };

    useEffect(() => {
        if (deleteResult) {
            setShowConfirmDelete(false);
            refreshHandler();
        }
    }, [deleteResult, refreshHandler]);

    return (
        <div className='text-slate-500 bg-slate-50 rounded-lg text-xs p-3 h-full'>
            <ConfirmDelete
                loading={deleteLoading}
                onDeleteHandler={confirmDelete}
                onShow={showConfirmDelete}
                setOnShow={setShowConfirmDelete}
                msg={t('product.Aredeletingproduct')}
            />

            <CardHeadeWithActions title={t('product.categorylist')}>
                <MwButton inGroup={true} type='saveBtn' onClick={() => setMode('Add')}>
                    <MdOutlineAddBox size={18} />
                    {t('product.createNewCategory')}
                </MwButton>
            </CardHeadeWithActions>

            <div className='py-5'>
                {loading ? (
                    <MwSpinner />
                ) : allCategories.length > 0 ? (
                    <div className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'>
                        <div className='max-h-[calc(100vh-280px)] overflow-auto custom-scrollbar'>
                            <table className='min-w-[760px] w-full border-collapse'>
                                <thead className='sticky top-0 z-10 bg-slate-100 text-xs uppercase tracking-wide text-slate-500'>
                                    <tr>
                                        <th className='px-3 py-3 text-center font-semibold'>#</th>
                                        <th className='px-3 py-3 text-start font-semibold'>
                                            {t('product.category_name_label')}
                                        </th>
                                        <th className='px-3 py-3 text-center font-semibold'>
                                            {t('product.websiteVisibility')}
                                        </th>
                                        <th className='px-3 py-3 text-center font-semibold'>
                                            {t('product.status')}
                                        </th>
                                        <th className='px-3 py-3 text-center font-semibold'>
                                            {t('table.actions')}
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {currentItems.map((el, index) => (
                                        <ListCategoriesItem
                                            index={indexOfFirstItem + index + 1}
                                            el={el}
                                            key={el._id}
                                            editHandeler={editHandeler}
                                            deleteHandeler={deleteHandeler}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className='flex flex-wrap items-center justify-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-4'>
                                <button
                                    onClick={() => {
                                        setCurrentPage(1);
                                        setItemsPerPage(allCategories.length || itemsPerPage);
                                    }}
                                    className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                                        itemsPerPage !== DEFAULT_ITEMS_PER_PAGE
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-white text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {t('common.all')}
                                </button>

                                {totalPages > 5 && currentPage > 3 && (
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        className='rounded-lg bg-white px-3 py-1.5 text-slate-600 transition-colors hover:bg-slate-200'
                                    >
                                        {i18n.language === 'ar' ? <MdKeyboardArrowRight size={20} /> : <MdKeyboardArrowLeft size={20} />}
                                    </button>
                                )}

                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter((number) => {
                                        if (totalPages <= 5) return true;
                                        if (number === 1 || number === totalPages) return true;
                                        return Math.abs(currentPage - number) < 3;
                                    })
                                    .map((number) => (
                                        <button
                                            key={number}
                                            onClick={() => {
                                                handlePageChange(number);
                                                setItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
                                            }}
                                            className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                                                currentPage === number && itemsPerPage === DEFAULT_ITEMS_PER_PAGE
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-white text-slate-600 hover:bg-slate-200'
                                            }`}
                                        >
                                            {number}
                                        </button>
                                    ))}

                                {totalPages > 5 && currentPage < totalPages - 2 && (
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        className='rounded-lg bg-white px-3 py-1.5 text-slate-600 transition-colors hover:bg-slate-200'
                                    >
                                        {i18n.language === 'ar' ? <MdKeyboardArrowLeft size={20} /> : <MdKeyboardArrowRight size={20} />}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <NoDataFound msg={error?.message || t('product.noCategories')} />
                )}
            </div>
        </div>
    );
};

export default ListCategories;
